<script lang="ts">
  import Form from "./components/Form/Form.svelte";
  import Failure from "./components/Failure/Failure.svelte";
  import { doctorStore } from "./store";
  import { onDestroy, onMount } from "svelte";
  import api from "./api";
  import { i18n, getLanguage, onLanguageChange } from "./i18n";
  import LanguageSelector from "./components/common/LanguageSelector.svelte";

  let fetchFailure: "setup" | "transient" | null = null;
  let isLoadingDoctor = false;
  // The retry button and the language selector can each start a load while the
  // other is still in flight, so only the newest one may touch the screen.
  let latestRequestId = 0;
  // We want the cool intro animation to run only once.
  let hasIntroAnimationBeenRun = false;

  // Asking the API for doctor "" would turn a broken link into a server error.
  const isBrokenLink = !api.getDoctorId();

  // The failure is cleared on success rather than on entry, so a retry that
  // runs into the full timeout keeps the message and the button on screen.
  async function loadDoctor() {
    const requestId = ++latestRequestId;
    isLoadingDoctor = true;
    try {
      const doctor = await api.getDoctor(getLanguage());
      if (requestId !== latestRequestId) return;

      doctorStore.set(doctor);
      fetchFailure = null;
    } catch (error) {
      if (requestId !== latestRequestId) return;

      fetchFailure = api.isTransientFailure(error) ? "transient" : "setup";
    } finally {
      if (requestId === latestRequestId) {
        isLoadingDoctor = false;
      }
    }
  }

  if (!isBrokenLink) {
    // The profile is rendered in the language the patient picked, so it is
    // re-requested whenever they change it.
    onMount(loadDoctor);
    onDestroy(onLanguageChange(loadDoctor));
  }

  function handleAnimationFinish() {
    hasIntroAnimationBeenRun = true;
  }
</script>

<svelte:head>
  <title>{$i18n.t("title")}</title>
</svelte:head>

<main>
  <div class="langWrap">
    <LanguageSelector />
  </div>
  {#if isBrokenLink}
    <Failure kind="brokenLink" />
  {:else if fetchFailure}
    <Failure
      kind={fetchFailure}
      disabled={isLoadingDoctor}
      on:click={loadDoctor}
    />
  {:else}
    <Form
      {hasIntroAnimationBeenRun}
      on:animationBeenRun={handleAnimationFinish}
    />
  {/if}
</main>

<style>
  .langWrap {
    padding: 10px 0;
  }
  .langWrap :global(.svelte-select) {
    border-radius: 3px !important;
  }

  main {
    align-items: center;
    display: flex;
    flex-direction: column;
  }

  @media (max-width: 500px) {
    main {
      height: 100%;
      width: 100%;
    }
  }

  @media (min-width: 500px) {
    main {
      border-radius: 16px;
      box-shadow:
        0 8px 16px rgba(0, 0, 0, 0.08),
        0 1px 4px rgba(0, 0, 0, 0.08);
      max-width: 400px;
      width: 100%;
    }
  }

  :global(:root) {
    --brand: 53, 117, 254;
    --black: #000;
    --blue: #00b5ce;
    --danger: #e20b22;
    --darkGrey: #858585;
    --green: #35cf4e;
    --lightBlack: #212121;
    --lightBlue: #f3f9fa;
    --primary: rgb(var(--brand));
    --silver: #f5f5f5;
    --primaryOpacity: rgba(var(--brand), 0.3);
  }
</style>
